import $ from 'jquery';

import { bindTrackUserEvents, trackStartChatSession } from 'chairisher/analytics/zendesk';
import { isTrade } from 'chairisher/context/auth';
import { isLiveChatActive, setLiveChatIsActive } from 'chairisher/context/site';
import { getLiveChatHelpSuggestionTag } from 'chairisher/context/zendesk';
import { isMinLargeDesktop } from 'chairisher/util/mediaquery';

/**
 * Background: the Zendesk web widget hurts our load time (even using their lazy load),
 * so we load a mock Zendesk button that manually bootstraps the plugin on click.
 * On some pages we also initialize an auto-bootstrap sequence set to a timer.
 * Support can then use the Zendesk backend to initialize proactive chat.
 * Usage: This class exports a singleton instance as zendeskWebWidgetInst.
 */
class ZendeskWebWidget {
    constructor() {
        this.trackingPosition = null;
    }

    /**
     * Binds the mock widget's onclick method and all corresponding actions
     */
    bind() {
        $('.js-livechat-widget').on('click', () => this.handleWindowLoad());
    }

    /**
     * Tracks interactions with the zendesk widgets
     */
    bindAnalytics() {
        if (isLiveChatActive()) {
            trackStartChatSession(this.trackingPosition);
            bindTrackUserEvents(this.trackingPosition);
        }
    }

    /**
     * Begins the animation, fetches the Zendesk chat window and sets the mock widget's removal from the dom
     * @param {boolean=} shouldAutoOpen Should auto-open the chat window on load.
     */
    bootstrapZendesk(shouldAutoOpen = true) {
        const $livechatWidget = $('#livechat-widget');
        $livechatWidget.addClass('livechat-widget-animation');

        this.loadZeSnippet(shouldAutoOpen);

        $livechatWidget.on('animationend', () => {
            $livechatWidget.remove();
        });
    }

    /**
     * Shows the messenger launcher and immediately opens the conversation window
     *
     * @see https://developer.zendesk.com/api-reference/widget-messaging/web/core/#open
     */
    displayChatWindow() {
        window.zE('messenger', 'show');
        window.zE('messenger', 'open');
    }

    /**
     * Runs full setup if initial load; otherwise, just displays the window
     */
    handleWindowLoad() {
        if (isLiveChatActive()) {
            this.displayChatWindow();
        } else {
            this.bootstrapZendesk();
        }
    }

    /**
     * Additional actions; fired in order upon the loading of the ze snippet
     * @param {boolean} shouldAutoOpen - Should auto-open the chat window on load.
     */
    handleZeSnippetLoad(shouldAutoOpen) {
        this.bindAnalytics();
        this.setConversationTags();
        if (shouldAutoOpen) {
            this.displayChatWindow();
        }
    }

    /**
     * Hides the real messenger launcher
     */
    hide() {
        if (isLiveChatActive()) {
            window.zE('messenger', 'hide');
        }
    }

    /**
     * Hides the mock web widget
     */
    hideMockWidget() {
        $('.js-livechat-widget').addClass('hidden');
    }

    /**
     * Updates the livechat context; then, creates and inserts the Zendesk chat snippet into the dom
     * @param {boolean} shouldAutoOpen - Should auto-open the chat window on load.
     */
    loadZeSnippet(shouldAutoOpen) {
        setLiveChatIsActive(true);

        const script = document.createElement('script');
        script.id = 'ze-snippet';
        script.src = 'https://static.zdassets.com/ekr/snippet.js?key=b5e0997a-0c9c-440d-b120-1e5a4c1bd1b1';
        script.onload = () => this.handleZeSnippetLoad(shouldAutoOpen);

        document.body.appendChild(script);
    }

    /**
     * Tags the conversation with the current page's support label
     *
     * @see https://developer.zendesk.com/api-reference/widget-messaging/web/core/#set-conversation-tags
     */
    setConversationTags() {
        const pageTag = getLiveChatHelpSuggestionTag();

        if (pageTag) {
            window.zE('messenger:set', 'conversationTags', [pageTag]);
        }
    }

    /**
     * @param {string} position The tracking position to include in analytics calls.
     */
    setTrackingPosition(position) {
        this.trackingPosition = position;
    }

    /**
     * Shows the real messenger launcher
     */
    show() {
        if (isLiveChatActive()) {
            window.zE('messenger', 'show');
        }
    }

    /**
     * Shows the mock web widget
     */
    showMockWidget() {
        $('.js-livechat-widget').removeClass('hidden');
    }

    /**
     * Start a timer to autoload the zendesk widget
     * @param {number=} timeout - Milliseconds to wait before auto-bootstrapping.
     * @param {boolean=} isOncePerSession - Only auto-boostrap once per session.
     */
    startAutoloadTimer(timeout = 15000, isOncePerSession = true) {
        if (isMinLargeDesktop() && !isTrade() && (!isOncePerSession || !window.sessionStorage.ZDAutobootstrapped)) {
            window.setTimeout(() => {
                window.sessionStorage.ZDAutobootstrapped = true;
                this.bootstrapZendesk(false);
            }, timeout);
        }
    }
}

export default new ZendeskWebWidget();
